import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Select, Button, Input, Modal, Space, message } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import {
  fetchLayerTemplates,
  createLayerTemplate,
} from "../../redux/actions/layerTemplateActions";
import data from "../../data/rawMaterialData";

/**
 * LayerTemplateSelector
 *
 * A template stores 4 layer-type configs: { top, flute, face, bottom }.
 * When applied, it expands those configs into the correct number of layers
 * for the current ply (derived from layers.length).
 *
 * Props:
 *  - ply           : number   – current ply value (3/5/7/9)  (= layers.length)
 *  - layers        : array    – current layers state
 *  - setLayers     : fn
 *  - gsm / setGsm  : array/fn
 *  - bf  / setBf   : array/fn
 */
const LayerTemplateSelector = ({
  ply,
  layers,
  setLayers,
  gsm,
  setGsm,
  bf,
  setBf,
}) => {
  const dispatch = useDispatch();
  const { list: templates, loading } = useSelector(
    (state) => state.layerTemplates
  );
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [saving, setSaving] = useState(false);

  // Fetch all templates once
  useEffect(() => {
    dispatch(fetchLayerTemplates());
  }, [dispatch]);

  // ── Expand a template's layer_config into the current ply ──────────
  const applyTemplate = (templateId) => {
    const tpl = (templates || []).find((t) => t.id === templateId);
    if (!tpl || !tpl.layer_config) return;
    if (!ply || ply < 3) {
      message.warning("Please select a ply value first");
      return;
    }

    const cfg = tpl.layer_config; // { top, flute, face, bottom }
    const newLayers = [];
    const gsmArr = [];
    const bfArr = [];

    // Build the type sequence for the current ply: top → [flute, face]… → flute → bottom
    const typeSeq = ["top"];
    for (let i = 1; i <= ply - 2; i++) {
      typeSeq.push(i % 2 === 0 ? "face" : "flute");
    }
    typeSeq.push("bottom");

    typeSeq.forEach((type, idx) => {
      const src = cfg[type] || {};
      newLayers.push({
        layerId: idx + 1,
        layerType: type,
        materialType: src.materialType || "",
        gsm: src.gsm || "",
        bf: src.bf || "",
        ff: type === "flute" ? (src.ff || "") : "",
      });

      // Populate the gsm/bf dropdown option arrays for each layer
      const matGsm = src.materialType && data.materialGsm[src.materialType]
        ? data.materialGsm[src.materialType]
        : [];
      const matBf = src.materialType && data.materialBf[src.materialType]
        ? data.materialBf[src.materialType]
        : [];
      gsmArr.push(matGsm);
      bfArr.push(matBf);
    });

    setLayers([...newLayers]);
    if (setGsm) setGsm([...gsmArr]);
    if (setBf) setBf([...bfArr]);
    message.success(`Template "${tpl.name}" applied`);
  };

  // ── Extract layer_config from current layers for saving ────────────
  const extractConfig = () => {
    if (!layers || layers.length === 0) return null;
    const cfg = {};
    for (const l of layers) {
      const type = l.layerType; // top, flute, face, bottom
      // Only store the first occurrence of each type (basic mode sets all flutes/faces the same)
      if (!cfg[type] && l.materialType) {
        cfg[type] = {
          materialType: l.materialType,
          gsm: l.gsm,
          bf: l.bf,
          ...(type === "flute" ? { ff: l.ff } : {}),
        };
      }
    }
    return cfg;
  };

  // ── Save handler ───────────────────────────────────────────────────
  const handleSave = async () => {
    if (!templateName.trim()) {
      message.warning("Please enter a template name");
      return;
    }
    const cfg = extractConfig();
    if (!cfg || !cfg.top) {
      message.warning("Fill at least the top layer before saving");
      return;
    }
    setSaving(true);
    try {
      await dispatch(
        createLayerTemplate({
          name: templateName.trim(),
          layer_config: cfg,
          description: templateDesc.trim() || null,
        })
      );
      setSaveModalOpen(false);
      setTemplateName("");
      setTemplateDesc("");
      dispatch(fetchLayerTemplates());
    } catch (_) {
      // toast already shown
    }
    setSaving(false);
  };

  if (!ply) return null;

  return (
    <>
      <Space style={{ marginBottom: 12, width: "100%" }} wrap>
        <Select
          placeholder="Load saved template…"
          style={{ minWidth: 250 }}
          loading={loading}
          allowClear
          showSearch
          optionFilterProp="children"
          onChange={applyTemplate}
          value={null}
          notFoundContent={loading ? "Loading…" : "No templates saved yet"}
        >
          {(templates || []).map((t) => {
            const cfg = t.layer_config || {};
            const summary = [
              cfg.top?.materialType,
              cfg.flute?.materialType,
              cfg.face?.materialType,
            ]
              .filter(Boolean)
              .join("/");
            return (
              <Select.Option key={t.id} value={t.id}>
                {t.name}{summary ? ` (${summary})` : ""}
              </Select.Option>
            );
          })}
        </Select>

        <Button
          icon={<SaveOutlined />}
          size="small"
          onClick={() => setSaveModalOpen(true)}
          disabled={!layers || layers.length === 0}
        >
          Save as Template
        </Button>
      </Space>

      <Modal
        title="Save Layer Template"
        open={saveModalOpen}
        onOk={handleSave}
        onCancel={() => setSaveModalOpen(false)}
        confirmLoading={saving}
        okText="Save"
      >
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontWeight: 600 }}>Template Name *</label>
          <Input
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder='e.g. "Kraft/Semi Standard"'
            maxLength={100}
          />
        </div>
        <div>
          <label style={{ fontWeight: 600 }}>Description (optional)</label>
          <Input.TextArea
            value={templateDesc}
            onChange={(e) => setTemplateDesc(e.target.value)}
            placeholder="Brief description of this combination"
            rows={2}
            maxLength={250}
          />
        </div>
        {layers && layers.length > 0 && (() => {
          const cfg = extractConfig();
          return cfg ? (
            <div style={{ marginTop: 16, fontSize: 12, color: "#888" }}>
              <strong>Config that will be saved:</strong>
              <ul style={{ paddingLeft: 16, marginTop: 4 }}>
                {["top", "flute", "face", "bottom"].map(
                  (type) =>
                    cfg[type] && (
                      <li key={type}>
                        <b>{type}:</b> {cfg[type].materialType} | GSM{" "}
                        {cfg[type].gsm} | BF {cfg[type].bf}
                        {type === "flute" ? ` | FF ${cfg[type].ff || "—"}` : ""}
                      </li>
                    )
                )}
              </ul>
              <em>This template can be applied to any ply (3/5/7/9).</em>
            </div>
          ) : null;
        })()}
      </Modal>
    </>
  );
};

export default LayerTemplateSelector;
